/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import * as React from 'react';
import { usePortal } from "../../components/_private/portal";
import { onNextFrame } from "../../utils/_private/animation";
const SCREEN_OFFSET = 16;
const topPos = ['top-start', 'top', 'top-end'];
const bottomPos = ['bottom-start', 'bottom', 'bottom-end'];
const startPos = ['start'];
const endPos = ['end'];
const order = {
    top: [...topPos, ...bottomPos, ...endPos, ...startPos],
    bottom: [...bottomPos, ...topPos, ...endPos, ...startPos],
    start: [...startPos, ...endPos, ...topPos, ...bottomPos],
    end: [...endPos, ...startPos, ...topPos, ...bottomPos],
};
/**
 * Get a position value which centers 2 elements vertically or horizontally
 */
const centerBySize = (originSize, targetSize) => {
    return Math.floor(originSize / 2 - targetSize / 2);
};
/**
 * Get an order of positions to try to fit popover on the screen based on its starting position
 */
const getPositionOrder = (position) => {
    const types = ['top', 'bottom', 'start', 'end'];
    const type = types.find(type => position.startsWith(type)) || 'bottom';
    return order[type];
};
/**
 * Workaround for ClientRect.width being readonly in C56
 */
const setClientRectWidth = (el, width) => {
    const rect = el.getBoundingClientRect();
    return {
        top: rect.top,
        right: rect.right,
        bottom: rect.bottom,
        left: rect.left,
        width: width,
        height: rect.height,
        x: rect.x,
        y: rect.y,
    };
};
/**
 * Check if element visually fits on the screen
 */
const fullyVisible = (bounds) => {
    const htmlEl = document.documentElement;
    const pageLeft = htmlEl.scrollLeft;
    const pageRight = pageLeft + htmlEl.clientWidth;
    const pageTop = htmlEl.scrollTop;
    const pageBottom = pageTop + htmlEl.clientHeight;
    return (bounds.left >= pageLeft &&
        bounds.left + bounds.width <= pageRight &&
        bounds.top >= pageTop &&
        bounds.top + bounds.height <= pageBottom);
};
/**
 * Calculate styles for the current position
 */
const calculatePosition = (originBounds, targetBounds, options) => {
    const { position: passedPosition, width, isInsidePortal, offset = [0, 0] } = options;
    let left = 0;
    let top = 0;
    let position = passedPosition;
    if (width === 'full' || width === 'trigger') {
        position = position.includes('top') ? 'top' : 'bottom';
    }
    switch (position) {
        case 'bottom':
        case 'top':
            left = centerBySize(originBounds.width, targetBounds.width) + originBounds.left + offset[0];
            break;
        case 'start':
            left = originBounds.left - targetBounds.width + offset[0];
            break;
        case 'end':
            left = originBounds.right + offset[0];
            break;
        case 'top-start':
        case 'bottom-start':
            left = originBounds.left + offset[0];
            break;
        case 'top-end':
        case 'bottom-end':
            left = originBounds.right - targetBounds.width + offset[0];
            break;
        default:
            left = offset[0];
            break;
    }
    switch (position) {
        case 'top':
        case 'top-start':
        case 'top-end':
            top = originBounds.top - targetBounds.height + offset[1];
            break;
        case 'bottom':
        case 'bottom-start':
        case 'bottom-end':
            top = originBounds.bottom + offset[1];
            break;
        case 'start':
        case 'end':
            top = centerBySize(originBounds.height, targetBounds.height) + originBounds.top + offset[1];
            break;
        default:
            top = offset[1];
            break;
    }
    if (top === undefined || left === undefined) {
        throw Error(`[Reshaped, flyout]: ${position} position is not valid`);
    }
    // When rendered inside portal, we don't need to accommodate for the page scroll
    top = Math.round(top + (isInsidePortal ? 0 : window.pageYOffset || 0));
    left = Math.round(left + (isInsidePortal ? 0 : window.pageXOffset || 0));
    let widthStyle = Math.ceil(targetBounds.width);
    const height = Math.ceil(targetBounds.height);
    if (width === 'full') {
        left = SCREEN_OFFSET;
        widthStyle = window.innerWidth - SCREEN_OFFSET * 2;
    }
    else if (width === 'trigger') {
        widthStyle = originBounds.width;
    }
    const styles = { left, top, width: widthStyle, height };
    return { styles, position };
};
/**
 * Order of keys here is responsible for the order of styles applied
 */
const defaultStyles = {
    left: -1000,
    top: -1000,
    width: 'auto',
    height: 'auto',
    position: 'absolute',
    // z-index doesn't accept strings
    zIndex: 90,
};
const resetStyles = {
    left: -1000,
    top: -1000,
    position: 'fixed',
    opacity: 0,
    animation: 'none',
    transition: 'none',
    zIndex: 80,
};
/**
 * Set position of the target element to fit on the screen
 */
const flyout = (origin, target, options) => {
    const { position, forcePosition, width } = options;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const targetClone = target.cloneNode(true);
    // FIXME: compatibility issue with Edge 16 and Chrome 56
    // eslint-disable-next-line compat/compat
    const originBounds = origin.getBoundingClientRect();
    // Reset all styles applied on the previous hook execution
    targetClone.style = '';
    Object.keys(resetStyles).forEach(key => {
        const value = resetStyles[key];
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        targetClone.style[key] = value === null || value === void 0 ? void 0 : value.toString();
    });
    if (width) {
        if (width === 'trigger') {
            targetClone.style.width = `${originBounds.width}px`;
        }
        else if (width !== 'full') {
            targetClone.style.width = width;
        }
    }
    document.body.appendChild(targetClone);
    const targetBounds = setClientRectWidth(target, targetClone.getBoundingClientRect().width);
    let calculated = calculatePosition(originBounds, targetBounds, options);
    if (!fullyVisible(calculated.styles) && !forcePosition) {
        const order = getPositionOrder(position);
        const mobileOrder = order.filter(position => position === 'top' || position === 'bottom');
        const test = (testOrder, extraOptions = {}) => {
            const { fullWidth } = extraOptions;
            testOrder.some(currentPosition => {
                const calculateOptions = Object.assign(Object.assign({}, options), { position: currentPosition });
                if (fullWidth)
                    calculateOptions.width = 'full';
                const tested = calculatePosition(originBounds, targetBounds, calculateOptions);
                if (fullyVisible(tested.styles)) {
                    calculated = tested;
                    return true;
                }
                return false;
            });
        };
        test(order);
        if (!fullyVisible(calculated.styles)) {
            test(mobileOrder, { fullWidth: true });
        }
    }
    targetClone.parentNode.removeChild(targetClone);
    return calculated;
};
const flyoutReducer = (state, action) => {
    switch (action.type) {
        case 'render':
            return Object.assign(Object.assign({}, state), { status: 'rendered' });
        case 'position':
            return Object.assign(Object.assign({}, state), { status: state.status === 'visible' ? 'visible' : 'positioned', position: action.payload.position, styles: Object.assign(Object.assign({}, defaultStyles), action.payload.styles) });
        case 'show':
            return Object.assign(Object.assign({}, state), { status: 'visible' });
        case 'hide':
            return Object.assign(Object.assign({}, state), { status: state.status === 'idle' ? 'idle' : 'hidden' });
        case 'remove':
            return Object.assign(Object.assign({}, state), { status: 'idle', styles: resetStyles });
        default:
            throw new Error('Invalid reducer type');
    }
};
export const useFlyout = (originRef, targetRef, options) => {
    const { position: defaultPosition = 'bottom', forcePosition, width, offset } = options;
    const { scopeRef } = usePortal();
    const isInsidePortal = !!(scopeRef === null || scopeRef === void 0 ? void 0 : scopeRef.current);
    const [state, dispatch] = React.useReducer(flyoutReducer, {
        position: defaultPosition,
        styles: defaultStyles,
        status: 'idle',
    });
    const render = React.useCallback(() => {
        dispatch({ type: 'render' });
    }, []);
    const show = React.useCallback(() => {
        dispatch({ type: 'show' });
    }, []);
    const hide = React.useCallback(() => {
        dispatch({ type: 'hide' });
    }, []);
    const remove = React.useCallback(() => {
        dispatch({ type: 'remove' });
    }, []);
    const updatePosition = React.useCallback(() => {
        if (!originRef.current || !targetRef.current)
            return;
        const nextFlyoutData = flyout(originRef.current, targetRef.current, {
            width,
            position: defaultPosition,
            forcePosition,
            isInsidePortal,
            offset,
        });
        dispatch({ type: 'position', payload: nextFlyoutData });
    }, [originRef, targetRef, defaultPosition, forcePosition, width, isInsidePortal, offset]);
    React.useEffect(() => {
        if (state.status === 'rendered')
            updatePosition();
        // Wait after positioning before show is triggered to animate flyout from the right side
        if (state.status === 'positioned') {
            onNextFrame(() => {
                updatePosition();
                show();
            });
        }
    }, [state.status, updatePosition, show]);
    return React.useMemo(() => ({
        position: state.position,
        styles: state.styles,
        status: state.status,
        updatePosition,
        render,
        hide,
        remove,
    }), [render, updatePosition, hide, remove, state.position, state.styles, state.status]);
};
